import {createContext, type FC, type PropsWithChildren, type RefObject, useContext, useRef, useState} from "react";

type ViewMode = 'tile' | 'list';

type GlobalContextType = {
    isActive: boolean;
    toggle: (state?: boolean) => void,
    panelRef: RefObject<HTMLDivElement | null>;
    listRef: RefObject<HTMLOListElement | null>;
    openPanel:()=>void,
    closePanel:()=>void,
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
};

const InstantSearchUIContext = createContext<GlobalContextType | undefined>(undefined);
const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
];
export const InstantSearchUIProvider: FC<PropsWithChildren> = ({children}) => {
    const [isActive, setIsActive] = useState(false);
    const [viewMode, setViewModeState] = useState<ViewMode>(() => {
        try {
            return (localStorage.getItem('algolia-view-mode') as ViewMode) || 'list';
        } catch {
            return 'list';
        }
    });
    const setViewMode = (mode: ViewMode) => {
        try { localStorage.setItem('algolia-view-mode', mode); } catch {}
        setViewModeState(mode);
    };
    const panelRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLOListElement | null>(null);
    const toggle = (state?: boolean) => {
        if (state !== undefined) {
            setIsActive(state);
        } else {
            setIsActive((prev) => !prev);
        }
    }
    const openPanel=()=>{
        setIsActive(true);
        if(panelRef.current){
            const firstFocusable =   panelRef.current?.querySelector<HTMLElement>(focusableSelectors.join(', '));
            if (firstFocusable) {
                firstFocusable?.focus({ preventScroll: true });
            }else{
                panelRef.current?.focus({ preventScroll: true });
            }
        }
    }
    const closePanel=()=>{
        setIsActive(false);
        if(listRef.current){
            listRef.current?.focus({ preventScroll: true });
        }
    }
    return (
        <InstantSearchUIContext.Provider value={{isActive, toggle, panelRef, openPanel, closePanel, listRef, viewMode, setViewMode}}>
            {children}
        </InstantSearchUIContext.Provider>
    );
};

export const useInstantSearchUIContext = () => {
    const context = useContext(InstantSearchUIContext);
    if (!context) throw new Error("useInstantSearchUIContext must be used within a InstantSearchUIProvider");
    return context;
};
